/**
 * Which of the portal's flagged-off features are switched on for this caller.
 *
 * Every field is required here and the whole object is optional on {@link Me}, which is the way
 * round that makes a missing answer safe: an API build that predates a flag sends no `features` at
 * all, and `me.features?.reportPacks` is then `undefined` — off. Defaulting the object to "all on"
 * would light up nav entries and routes whose endpoints that build does not serve.
 */
export interface Features {
  reportPacks: boolean;
  customViews: boolean;
}

export interface Me {
  objectId: string;
  displayName: string;
  email: string;
  isAdmin: boolean;
  isAccountManager: boolean;
  /** See {@link Features}. Absent from an older API build, and absent means everything off. */
  features?: Features;
  /**
   * The signed-in user's down-level Windows account name (e.g. `dmorris`), from Active Directory
   * via Entra. Null when the directory holds none or could not be asked.
   *
   * Seeds a report-parameter lookup, and — on create only — is STORED as the value of a report's
   * identity parameter. It used to say it was never written into a subscription, on the reasoning
   * that what a report is asked to list values for and what a subscription runs under are two
   * different things. On /Sales/Scope 3 that separation is what broke: the lists were resolved as
   * the signed-in user, nothing was stored, and every run then resolved the identity parameter as
   * the portal's service account instead and failed with "Default value or value provided for the
   * report parameter 'CountryCode' is not a valid value". See identityParameter.ts for which
   * parameters this reaches and why the rule is deliberately narrow.
   */
  windowsAccountName?: string | null;
}

export type SubscriptionRunStatus =
  | 'Unknown' | 'Success' | 'Failed' | 'Disabled' | 'Pending' | 'Stale';

export interface SubscriptionSummary {
  subscriptionId: string;
  reportPath: string;
  reportName: string;
  scheduleDescription: string;
  recipientCount: number;
  recipientPreview?: string | null;
  lastRunUtc?: string | null;
  nextRunUtc?: string | null;
  lastStatus: SubscriptionRunStatus;
  isEnabled: boolean;
  ownerDisplayName?: string | null;
  ownerObjectId?: string | null;
  /**
   * Where this one's output goes besides the inbox, and whether that route is intact. Absent for
   * the overwhelming majority, which are email-only. On the list rather than only the detail page
   * because "does this file to pickup?" should not require opening anything.
   */
  pickup?: SubscriptionPickupState | null;
  /**
   * The PORTAL's own name for whoever `ownerObjectId` points to — NOT the same thing as
   * `ownerDisplayName` above, which is (and always stays) the report server's own record: a
   * Windows account, and the identity MANDW01 actually sends mail as. This is who the portal's
   * ownership record names, which an admin reassignment changes; `ownerDisplayName` does not move
   * when it does.
   *
   * Never render one in place of the other — see the "Portal owner" / "Sends as (MANDW01)" columns
   * on the admin page for why they are shown side by side rather than merged.
   */
  portalOwnerDisplayName?: string | null;
  /** The subscription's own name. See {@link SubscriptionDetail.displayName}. */
  displayName?: string | null;
  /**
   * Every direct recipient, not just `recipientPreview`'s first one — carried so the list can be
   * SEARCHED by recipient, which is how anyone finds "the one that mails Jane" among rows that
   * differ only by who receives them. The cell still renders one address and a "+N more".
   *
   * Null on a row from a path that predates the field. A search must degrade to matching nothing
   * for that row rather than throwing, which is why every read of it goes through a `?? []`.
   */
  recipientEmails?: string[] | null;
}

/**
 * The filters and the offset for one page of a subscription list.
 *
 * Every field is a server-side filter, and that is not an optimisation — it is what makes paging
 * possible at all. A search or a status chip applied in the browser can only ever narrow the fifty
 * rows already loaded, so it would find nothing on page four and its counts would describe the page
 * rather than the list. Whatever narrows the list has to narrow it before the page is cut.
 *
 * `status` is comma-separated (`'Failed,Unknown'`) because that is the shape the page already keeps
 * in its own query string, so a link somebody pasted and the API call it produces agree.
 */
export interface SubscriptionListParams {
  search?: string;
  status?: string;
  /** Admin-only narrowing to the caller's own rows; a no-op for everyone else. */
  mine?: boolean;
  /** Admin list only: matches either owner name, the portal's record or the report server's. */
  owner?: string;
  skip?: number;
  take?: number;
}

/**
 * How many subscriptions sit in each status for the current filter, ignoring the status filter
 * itself — so the chips hold still as they are toggled, and their sum is the "All" chip's number.
 */
export interface SubscriptionStatusCount {
  status: SubscriptionRunStatus;
  total: number;
}

/**
 * One report-server owner's subscriptions, counted over the whole list rather than the page.
 * `name` is null where the report server holds no owner — rendered as a note, never offered as
 * something to filter by, because there is no name to filter on.
 */
export interface SubscriptionOwnerGroup {
  name?: string | null;
  total: number;
  failing: number;
}

/**
 * One page of a subscription list plus the numbers the screen around it needs.
 *
 * `total` is what matches the filters — what the pager steps through. `totalUnfiltered` is every
 * subscription the caller may see. They are different numbers and both are shown: "Showing 12 of
 * 282" is the two of them side by side.
 */
export interface SubscriptionListResponse {
  subscriptions: SubscriptionSummary[];
  total: number;
  skip: number;
  take: number;
  totalUnfiltered: number;
  /** How many of `totalUnfiltered` the portal records as the caller's own. */
  mineCount: number;
  statusCounts: SubscriptionStatusCount[];
  ownerGroups: SubscriptionOwnerGroup[];
}

/**
 * What to call a subscription on screen.
 *
 * Its own name when it has one, the report's name when it does not — which is every subscription
 * created before names existed. Never the only thing shown: the report path goes beside it
 * wherever this is used, because a named subscription that does not say which report it runs is a
 * label with nothing behind it.
 */
export function subscriptionTitle(sub: { displayName?: string | null; reportName: string }): string {
  return sub.displayName?.trim() || sub.reportName;
}

export interface RecipientGroup { objectId: string; displayName: string; }

/**
 * Cc/Bcc/replyTo are their own delivery fields on the report server, not a flavour of `emails`.
 * A save sends all of them: PBIRS replaces the whole delivery block, so anything left out is
 * deleted rather than preserved.
 */
export interface SubscriptionRecipients {
  emails: string[];
  groups: RecipientGroup[];
  cc?: string[] | null;
  bcc?: string[] | null;
  replyTo?: string | null;
}

/**
 * One parameter as the subscription has it set. `values` is a list because SSRS parameters can be
 * multi-valued; `fromField` replaces it on a data-driven subscription, where the value comes from
 * a named column of the subscription's own query.
 */
export interface ReportParameterValue { name: string; values: string[]; fromField?: string | null; }

export type ScheduleFrequency = 'Once' | 'Hourly' | 'Daily' | 'Weekly' | 'Monthly' | 'Cron';

export interface ScheduleDefinition {
  frequency: ScheduleFrequency;
  timeOfDay: string;
  timeZoneId: string;
  intervalDays?: number | null;
  daysOfWeek?: number | null;
  dayOfMonth?: number | null;
  monthInterval?: number | null;
  /**
   * Which months a monthly schedule fires in, as the report server's own bitmask: bit 0 = January
   * through bit 11 = December, so 4095 is every month and 585 is Jan/Apr/Jul/Oct. Null means every
   * month.
   *
   * Same layout as `dbo.Schedule.Month` on the server and as `MonthFlag` in the API, deliberately —
   * there is no conversion anywhere in the chain, so there is no conversion to get wrong. One bit
   * separates Jan/Apr/Jul/Oct from Feb/May/Aug/Nov and both are in live use.
   */
  months?: number | null;
  startDate?: string | null;
  endDate?: string | null;
  cronExpression?: string | null;
  humanReadable?: string | null;
}

/** Every month selected — the value an ordinary monthly schedule carries. */
export const ALL_MONTHS = 4095;

export interface SubscriptionMetadata {
  ownerObjectId: string;
  ownerDisplayName: string;
  ownerGroupId?: string | null;
  createdUtc: string;
  createdBy: string;
  lastEditedUtc?: string | null;
  lastEditedBy?: string | null;
  totalRuns: number;
  successRate: number;
  etagHash: string;
  /** See {@link SubscriptionSummary.portalOwnerDisplayName} — same field, same caveat. */
  portalOwnerDisplayName?: string | null;
}

export interface SubscriptionDetail {
  subscriptionId: string;
  reportPath: string;
  reportName: string;
  emailSubject: string;
  comment: string;
  renderFormat: string;
  priority: string;
  includeReport: boolean;
  includeLink: boolean;
  recipients: SubscriptionRecipients;
  schedule: ScheduleDefinition;
  parameters: ReportParameterValue[];
  /** What the report declares about those parameters — carried here so the page needs no second call. */
  parameterDefinitions?: ReportParameterDefinition[] | null;
  /** Recipients and parameter values come from a query, not a fixed list. The portal cannot save these. */
  isDataDriven?: boolean;
  /**
   * Set when the schedule uses an SSRS recurrence the editor cannot hold — several days of the
   * month, "nth weekday of the month", or an every-N-weeks stride. Reads as a reason, e.g. "it runs
   * on several days of the month (1, 3, 4, 5), and the editor only supports one". Saving is refused
   * by the API as well as disabled here, because the editor can only show a collapsed version and
   * saving would write the collapsed version back.
   */
  uneditableScheduleReason?: string | null;
  /**
   * Where this one's output goes besides the inbox. Unlike the list's copy, this is verified
   * against the report server field by field, so it can say "the schedule no longer matches"
   * rather than only "the companion still exists".
   */
  pickup?: SubscriptionPickupState | null;
  metadata: SubscriptionMetadata;
  isEnabled: boolean;
  lastRunUtc?: string | null;
  nextRunUtc?: string | null;
  lastStatus: SubscriptionRunStatus;
  /**
   * What to call this subscription when the report's own name is not enough to tell it apart —
   * which is the usual case, since the same report is subscribed to many times over for different
   * clients.
   *
   * This IS the report server's own subscription description, not a portal-side label: PBIRS
   * already writes one ("Send e-mail to a@x, b@x"), so a name typed here replaces that generated
   * string rather than competing with it, and Report Server Schedule Manager shows the same name
   * the portal does. Null or blank means it has none — fall back to the report name via
   * {@link subscriptionTitle}.
   */
  displayName?: string | null;
}

export interface CreateSubscriptionRequest {
  reportPath: string;
  emailSubject: string;
  comment?: string;
  renderFormat: string;
  recipients: SubscriptionRecipients;
  schedule: ScheduleDefinition;
  parameters?: ReportParameterValue[];
  priority?: string;
  includeReport?: boolean;
  includeLink?: boolean;
  /** See {@link RecipientCheckResult.fingerprint}. */
  confirmRecipientMismatch?: string | null;
  /**
   * "…and also file every run into report pickup", chosen alongside the recipients on the create
   * form. The API creates the companion file-share subscription and decides where it writes —
   * this only says yes/no and which client to file it under.
   *
   * Only on create. Turning pickup on or off for a subscription that already exists creates or
   * deletes a subscription on the report server, so it is its own action on the detail page rather
   * than something that rides along with an unrelated edit to the subject line.
   */
  pickupDelivery?: PickupDeliveryRequest | null;
  /**
   * See {@link SubscriptionDetail.displayName}. Null or blank leaves the name to PBIRS, which
   * generates its own — the pre-existing behaviour, and still the default.
   */
  displayName?: string | null;
}

/**
 * Whether the pickup route a create asked for was actually set up.
 *
 * `Configured` and `NotRequested` are the two answers that mean nothing is wrong. The other two
 * both mean the subscription exists and emails, and only the file half is missing — which is why
 * the create still counts as having succeeded.
 */
export type PickupSetupOutcome = 'NotRequested' | 'Configured' | 'Unconfigured' | 'Failed';

/**
 * What a create actually achieved.
 *
 * A bare id was not enough: the email subscription and its pickup companion are two subscriptions
 * on MANDW01 with no transaction between them, and the second is allowed to fail without failing
 * the first. Returning only the id made a create whose pickup route never happened look exactly
 * like one that worked.
 */
export interface CreateSubscriptionResult {
  id: string;
  pickup: PickupSetupOutcome;
  /** Why pickup is not set up, in words. Null unless `pickup` says something went wrong. */
  pickupProblem?: string | null;
}

/** The pickup settings carried on a create. Omitted or `enabled: false` means email only. */
export interface PickupDeliveryRequest {
  enabled: boolean;
  clientKey?: string | null;
  clientName?: string | null;
  /**
   * The exact head office id, from the Profiles API (see {@link HeadOfficeMatch}) — resolved by
   * the portal when it is not supplied, but supplying it from a chosen typeahead match is what
   * guarantees the folder name is exact rather than a best-effort name-only lookup.
   */
  headOfficeId?: string | null;
}

export interface UpdateSubscriptionRequest {
  emailSubject: string;
  comment?: string;
  renderFormat: string;
  recipients: SubscriptionRecipients;
  schedule: ScheduleDefinition;
  parameters?: ReportParameterValue[];
  ifMatchEtag?: string;
  priority?: string;
  includeReport?: boolean;
  includeLink?: boolean;
  /** See {@link RecipientCheckResult.fingerprint}. */
  confirmRecipientMismatch?: string | null;
  /**
   * See {@link SubscriptionDetail.displayName}. Like every other field on this request it REPLACES
   * what is there, because a PBIRS update rewrites the whole subscription — so it has to be sent on
   * every save, not only when the name is what changed, or saving anything else clears the name.
   */
  displayName?: string | null;
}

/** Why one recipient does not look right for the client the report's parameters name. */
export type RecipientMismatchKind = 'UnrecognisedForClient' | 'PersonalWebmail';

export interface RecipientMismatch {
  email: string;
  domain: string;
  /** Which delivery field it came from — To, Cc or Bcc. */
  field: string;
  kind: RecipientMismatchKind;
}

/**
 * Whether these recipients belong with these parameters.
 *
 * `checked` is the part to read first. The check can only run when the report's parameters name a
 * client AND that client has people on record, so `checked: false` means "no opinion", not "fine" —
 * it must never be rendered as an all-clear.
 */
export interface RecipientCheckResult {
  checked: boolean;
  mismatches: RecipientMismatch[];
  /** The client ids read out of the parameters. */
  clientScope: string[];
  /** The domains on record for that client. */
  knownDomains: string[];
  reason?: string | null;
  /**
   * Identifies this exact set of mismatches. Sent back as `confirmRecipientMismatch` to say "yes,
   * send it anyway" — and because it covers the specific addresses, adding another recipient
   * invalidates it and the question gets asked again.
   */
  fingerprint?: string | null;
}

export interface ReportCatalogItem {
  path: string;
  name: string;
  description?: string | null;
  createdUtc: string;
  modifiedUtc: string;
  type: string;
  hidden: boolean;
}

export interface ReportParameterValidValue { label: string; value: string; }

export interface ReportParameterDefinition {
  name: string;
  displayName: string;
  /** The SSRS parameter type: String, DateTime, Boolean, Integer or Float. */
  type: string;
  nullable: boolean;
  multiValued: boolean;
  allowBlank: boolean;
  defaultValues: string[];
  validValues?: ReportParameterValidValue[] | null;
  /** False for parameters the report fills in itself; they are shown but not offered for editing. */
  promptUser: boolean;
  /** The allowed values come from a dataset the report runs, so the catalog has no list to offer. */
  dynamicValidValues: boolean;
  /**
   * The parameters this one's own dataset takes as arguments. Changing any of them invalidates
   * this parameter's list, which is what makes View -> Country -> Head Office cascade.
   */
  dependencies?: string[] | null;
  /**
   * The report computes its default when it runs, so `defaultValues` is empty and yet the report
   * does have one. Only `ResolvedParameterValues.defaultValues` can say what it currently is.
   * Seven of the nine parameters on "Any Client Detail (Custom View)" are this shape.
   */
  hasDynamicDefault: boolean;
}

/** One parameter's allowed values as the report server resolved them for the current choices. */
export interface ResolvedParameterValues {
  name: string;
  validValues: ReportParameterValidValue[];
  /** How many matched before the server's cap, so a partial list can say that it is one. */
  totalCount: number;
  truncated: boolean;
  /**
   * What the report would use for this parameter if the subscription supplied nothing — resolved
   * just now, so it covers the expression-based defaults the catalogue cannot hold. It moves: on
   * the live report FromDate resolves to today and ToDate to today + 60 days. Never render it
   * anywhere it could be mistaken for the value stored on the subscription.
   */
  defaultValues?: string[] | null;
}

export interface ResolveParameterValuesRequest {
  path: string;
  values?: ReportParameterValue[];
  /** Filters labels and values before the cap. For lists too long to send whole. */
  search?: string;
  /** Narrows the answer to one parameter, so a search in one field cannot filter another's list. */
  parameter?: string;
  /**
   * What this form already holds for each parameter. The server guarantees an option matching each
   * of these comes back, whatever the cap and whatever `search` says.
   *
   * Not the same field as `values`, and not a candidate for being merged into it. `values` is the
   * upstream cascade input — it decides what the report server resolves, and it is part of the
   * server's cache key. This decides only which rows of an already-resolved list are worth sending,
   * so it is deliberately excluded from that key.
   *
   * Without it the editor cannot render a stored value honestly. The list arrives capped at 500 of
   * 5,846 head offices, `optionFor` finds nothing for the 199159 the subscription actually stores,
   * and the field reports it as "(no longer offered)" beside an offer to remove it — for a head
   * office that is perfectly current and simply sorted past the cap.
   */
  selected?: ReportParameterValue[];
}

/** How firm the portal's guess at a run's owner is. See RunOwnerSource on the API side. */
export type RunOwnerSource =
  | 'Unknown' | 'Interactive' | 'Subscription' | 'Report' | 'Ambiguous';

/** One report parameter as a run actually used it; multi-valued parameters carry several values. */
export interface RunParameter { name: string; values: string[]; }

export interface RunHistoryEntry {
  executionId: number;
  subscriptionId?: string | null;
  reportPath: string;
  reportName: string;
  startedUtc: string;
  durationMs: number;
  status: SubscriptionRunStatus;
  recipientCount?: number | null;
  errorMessage?: string | null;
  source?: string | null;
  /** The person answerable for the run — not necessarily the account that executed it. */
  ownerUserName?: string | null;
  ownerSource: RunOwnerSource;
  /** The Windows account the report server ran under; the service account for every subscription. */
  executedByAccount?: string | null;
  parameters: RunParameter[];
  parametersTruncated: boolean;
}

/**
 * One page of the execution log. `total` is the count of rows matching the same filters, before
 * paging — the run history page has ~19,937 to walk, so `entries.length` says nothing about how
 * far it goes and the <Pager> needs the real figure to render "1–50 of 19,937".
 *
 * `total` is counted before the API applies its catalogue-visibility trim, so for a caller who
 * cannot see part of the estate it can name more rows than they will be shown. See
 * RunHistoryController for why that is the least-wrong of the options.
 */
export interface RunHistoryPage {
  entries: RunHistoryEntry[];
  total: number;
  skip: number;
  take: number;
}

export interface SchedulePreview {
  humanReadable: string;
  nextRunsUtc: string[];
  warning?: string | null;
}

export interface OutboxEmail {
  id: string;
  sentUtc: string;
  subscriptionId?: string | null;
  reportName: string;
  subject: string;
  to: string[];
  renderFormat: string;
  source: string;
}

export interface KtInsightsLastRun {
  requestedUtc?: string | null;
  datasetRefreshedUtc?: string | null;
  datasetRefreshStatus?: string | null;
  datasetCheckedUtc?: string | null;
  datasetCheckStatus?: string | null;
  sentUtc?: string | null;
  exportStatus?: string | null;
  outcome: SubscriptionRunStatus;
}

export interface KtInsightsDelivery {
  scheduleId: number;
  exportName: string;
  exportType: string;
  live: boolean;
  triggerDescription: string;
  ownerUser?: string | null;
  recipients: string[];
  emailSubject?: string | null;
  outputFormat?: string | null;
  destinationSummary?: string | null;
  lastRun?: KtInsightsLastRun | null;
}

export interface KtInsightsDeliveriesResponse {
  source: 'live' | 'sample' | 'unconfigured';
  deliveries: KtInsightsDelivery[];
}

/**
 * One export request from PowerBI.PBRS.Export — every KT Insights export the estate has
 * run, whoever triggered it. `exportStatus` is MANDW01's own wording; `outcome` is that
 * normalised onto the portal's status vocabulary with age taken into account, so an
 * export left at 'Created' long enough that it will never complete reads as Stale.
 */
export interface KtInsightsExport {
  exportId: number;
  exportName: string;
  reportName?: string | null;
  exportType?: string | null;
  recipient?: string | null;
  cc?: string | null;
  requestedUtc?: string | null;
  startedUtc?: string | null;
  finishedUtc?: string | null;
  exportStatus?: string | null;
  outcome: SubscriptionRunStatus;
  statusDetail?: string | null;
  pageList?: string | null;
  filterSummary?: string | null;
  userCheckExportId?: number | null;
  userCheckStatus?: string | null;
}

export interface KtInsightsExportStatusCount {
  status: string;
  total: number;
  neverStarted: number;
  latestRequestedUtc?: string | null;
}

export interface KtInsightsExportHistoryResponse {
  source: 'live' | 'sample' | 'unconfigured';
  exports: KtInsightsExport[];
  statusCounts: KtInsightsExportStatusCount[];
  total: number;
  skip: number;
  take: number;
  scopedToRecipient: boolean;
}

/**
 * A KT Insights report that can be scheduled, from Embed.Report on MANDW01.
 *
 * `selectablePageCount` is the length of the page list this report opens, not the number of pages
 * it has — so "21 pages" beside a report and 21 tickboxes under it are the same number by
 * construction.
 */
export interface KtInsightsReport {
  reportId: number;
  reportName: string;
  selectablePageCount: number;
}

/**
 * One page a user can tick.
 *
 * `pageOrderId` is Embed.Page.DefaultPageOrder — the only part of this that reaches MANDW01, and
 * the reason the list used to say "pages 1,2,7". `viewLabel` is the page's axis within its section
 * ("Booker Summary") and `pageName` the whole name ("Travel Spend by Booker").
 */
export interface KtInsightsReportPage {
  pageOrderId: number;
  pageName: string;
  section: string;
  viewLabel?: string | null;
}

/**
 * `source` is 'live' or 'unavailable', and the difference matters: an empty list from an estate
 * that answered means there is nothing to schedule, one from an estate that did not means nothing
 * is known. The picker says so rather than rendering an empty list either way.
 */
export interface KtInsightsReportsResponse {
  source: 'live' | 'unavailable';
  reports: KtInsightsReport[];
}

export interface KtInsightsReportPagesResponse {
  reportId: number;
  source: 'live' | 'unavailable';
  pages: KtInsightsReportPage[];
}

export type KtInsightsDateRange =
  | 'None'
  | 'Last7Days'
  | 'Last30Days'
  | 'Last90Days'
  | 'PreviousMonth'
  | 'PreviousQuarter'
  | 'YearToDate'
  | 'Custom';

/**
 * One filter this report can be sliced by, and the values the estate accepts for it — read from
 * Embed.StaticFilter, the same config the KT Insights front-end drives its filter bar from.
 *
 * `key` is the estate's stable identity ("SavingsType"); `label` is display text ("Savings Type")
 * and is what the recipient sees in the delivered email's "Filters applied" line.
 */
export interface KtInsightsFilterOption {
  key: string;
  label: string;
  table: string;
  column: string;
  operator: string;
  values: string[];
  defaultValues: string[];
}

/** A filter the user set: the estate's target and label, with the values they picked. */
export interface KtInsightsFilterSelection {
  key: string;
  label: string;
  table: string;
  column: string;
  operator: string;
  values: string[];
}

export interface KtInsightsReportFiltersResponse {
  reportId: number;
  source: 'live' | 'unavailable';
  filters: KtInsightsFilterOption[];
}

/**
 * What the export runs for. Relative ranges are resolved fresh on every dispatch.
 *
 * `headOffice` and `dateRange` stay named while everything else lives in `filters`, which is the
 * line between filters the estate can enumerate and filters it cannot: there are ~13,000 head
 * offices (hence a typeahead) and a date range is a rule resolved at dispatch, not a value.
 */
export interface KtInsightsParameters {
  headOffice?: string | null;
  dateRange: KtInsightsDateRange;
  customFrom?: string | null;
  customTo?: string | null;
  /** Absent on subscriptions created before report filters existed — read as "none". */
  filters?: KtInsightsFilterSelection[] | null;
}

export interface KtInsightsHeadOffice {
  name: string;
  region?: string | null;
  headOfficeId?: number | null;
}

/**
 * Whether the estate could be asked about a recipient, and what it said.
 *
 * `NotAUser` and `Unavailable` are not interchangeable: the first is a fact about the
 * person, the second is a fact about the portal's connection to MANDW01. Rendering the
 * second as the first tells an account manager their colleague has no KT Insights access
 * when nothing of the sort is known.
 */
export type KtInsightsRecipientStatus = 'Resolved' | 'NotAUser' | 'Unavailable';

/** What KT Insights knows about a candidate recipient — drives the warnings on the form. */
export interface KtInsightsRecipient {
  email: string;
  status: KtInsightsRecipientStatus;
  /** True only for `Resolved`. Check `status` before wording anything for a human. */
  isKnown: boolean;
  couldNotCheck: boolean;
  displayName?: string | null;
  roleName?: string | null;
  isInternal: boolean;
  accessiblePageCount: number;
  deliverablePageOrderIdsCsv?: string | null;
  warnings: string[];
}

/**
 * What became of a queued export, per PowerBI.PBRS.Export on MANDW01.
 *
 * Two of these carry the weight. 'Discarded' means the KT Insights pipeline caught an error,
 * deleted the queued request and printed the reason where nobody sees it — the portal's dispatch
 * succeeded, but no export exists and no email will ever arrive.
 *
 * 'Accepted' is the opposite fact and replaces the 'Waiting' this used to have: the pipeline holds
 * the request and never writes an outcome back for exports the portal queued, so the row sits at
 * 'Created' for ever — including for exports whose recipient has the email in front of them. It
 * means handed over, and nothing beyond that. Only 'Delivered' means delivered.
 */
export type KtInsightsExportOutcome =
  | 'Unknown' | 'Queued' | 'Discarded' | 'Accepted'
  | 'Stalled' | 'Rendering' | 'Delivered' | 'NoAccess' | 'Failed';

export interface KtInsightsExportStatus {
  queueId: number;
  outcome: KtInsightsExportOutcome;
  summary: string;
  exportStatusRaw?: string | null;
  exportType?: string | null;
  queuedUtc?: string | null;
  startedUtc?: string | null;
  finishedUtc?: string | null;
  isSettled: boolean;
}

/** What "Run now" actually achieved — the queue id is the handle on everything after. */
export interface KtInsightsRunNowResult {
  subscriptionId: string;
  status: SubscriptionRunStatus;
  queueId?: number | null;
  ranUtc: string;
  error?: string | null;
}

export interface KtInsightsSubscription {
  id: string;
  /**
   * Historical, and empty on everything created since the page picker: the key of the preset a
   * pre-picker row was built from. Nothing in the UI decides anything from it.
   */
  presetKey: string;
  /** The export's title — derived from the report and pages chosen, or typed by whoever made it. */
  presetTitle: string;
  reportId: number;
  reportName: string;
  pageOrderIdsCsv: string;
  /**
   * The page names as Embed.Page gave them when the export was saved. Empty, or a different length
   * from `pageOrderIdsCsv`, on old rows whose labels were a hand-written array — see
   * `describePages` in the KT Insights page for what is shown then.
   */
  pageLabels: string[];
  recipientEmail: string;
  ownerObjectId: string;
  ownerDisplayName: string;
  schedule: ScheduleDefinition;
  scheduleDescription: string;
  isEnabled: boolean;
  nextRunUtc?: string | null;
  lastRunUtc?: string | null;
  lastStatus: SubscriptionRunStatus;
  lastError?: string | null;
  createdUtc: string;
  parameters: KtInsightsParameters;
  parametersDescription: string;
  recipientDisplayName?: string | null;
  lastQueueId?: number | null;
  lastQueuedUtc?: string | null;
  /** Resolved from MANDW01 per request; null when it has never run or could not be read. */
  lastExport?: KtInsightsExportStatus | null;
}

export interface CreateKtInsightsSubscriptionRequest {
  reportId: number;
  /** CSV of `pageOrderId`s — exactly what PBRS.Queue.DefaultPageOrderIds takes. */
  pageOrderIdsCsv: string;
  schedule: ScheduleDefinition;
  parameters?: KtInsightsParameters | null;
  recipientEmail?: string | null;
  /** Optional; left off, the API derives one from the report and the pages chosen. */
  title?: string | null;
}

/**
 * An edit to an existing scheduled export.
 *
 * No report and no pages, and their absence is the contract rather than an oversight: changing
 * which report and pages go out does not adjust an export, it replaces it with a different one,
 * and the delivery history hanging off the row would then describe a document nobody scheduled.
 * That is a delete and a create.
 *
 * `parameters: null` means "no filters" — the same as on create. It does NOT mean "leave the
 * existing filters alone": an edit states the whole desired state, so clearing the head office in
 * the form clears it on the subscription.
 */
export interface UpdateKtInsightsSubscriptionRequest {
  schedule: ScheduleDefinition;
  parameters?: KtInsightsParameters | null;
  recipientEmail?: string | null;
}

export interface AuditEntry {
  id: number;
  occurredUtc: string;
  actorObjectId: string;
  actorDisplayName: string;
  action: string;
  subscriptionId?: string | null;
  summary?: string | null;
  beforeJson?: string | null;
  afterJson?: string | null;
  correlationId?: string | null;
}

// ── Admin: owner reassignment ──────────────────────────────────────────────────────────

/**
 * One match from the owner-reassignment picker's search — an Entra identity, but only ever one
 * the report server's own `dbo.Users` table also recognises. See `ReportServerUserDirectory` on
 * the API side for why a plain Entra-tenant search isn't what backs this.
 */
export interface DirectoryUser {
  objectId: string;
  displayName: string;
  mail?: string | null;
}

/** One subscription's outcome from a bulk reassignment — never all-or-nothing. */
export interface BulkReassignOwnerResult {
  subscriptionId: string;
  success: boolean;
  error?: string | null;
}

// ── Report pickup ──────────────────────────────────────────────────────────────────────
// Scheduled report output delivered to file storage, searchable and downloadable here.

export type PickupProducer = 'Pbirs' | 'KtInsights';

/**
 * Available = on the share, whichever year folder it is in.
 * Missing = gone from the share. There is no Expired: nothing in pickup is ever deleted or moved,
 * so a missing file means somebody removed it from the share outside the portal.
 */
export type PickupFileState = 'Available' | 'Missing';

export interface PickupFileSummary {
  id: string;
  producer: PickupProducer;
  reportName: string;
  reportPath?: string | null;
  clientName?: string | null;
  clientKey?: string | null;
  /** Dataset.Customer.HeadOfficeID — what makes a client unambiguous, and what leads its folder. */
  headOfficeId?: string | null;
  periodLabel?: string | null;
  /** Sortable form of the same period ("2026-07") — the token that leads the file name. */
  periodKey?: string | null;
  producedUtc: string;
  format: string;
  formatLabel: string;
  fileName: string;
  sizeBytes: number;
  /** From the report server's execution log. Null when the run could not be matched, and for every KT Insights file. */
  rowCount?: number | null;
  state: PickupFileState;
  /** The delivery, not the subscription — the subscription id lives on the delivery record. */
  deliveryId?: string | null;
  runExecutionId?: string | null;
  runStatus?: SubscriptionRunStatus | null;
  downloadCount: number;
  lastDownloadedUtc?: string | null;
  /**
   * Who this file was RENDERED AS. A KT Insights export renders as its recipient: a row-level
   * filter is bound to that person's email for every role except Internal, and per-user page
   * permissions decide what the file contains at all. So the CONTENTS depend on this, and two
   * files from the same preset and period are not interchangeable.
   *
   * Null for PBIRS, which renders under the report server's own account and scopes by report
   * parameters instead. That is a real distinction, stated out loud rather than left blank.
   */
  renderedAsEmail?: string | null;
  renderedAsName?: string | null;
  /** PowerBI.Embed.User.RoleId. 1 = Internal (all data); 2 = external head office; 3 = external department. */
  renderedAsRoleId?: number | null;
  renderedAsRoleName?: string | null;
  /**
   * The year folder the file is in — the year its RUN fired, which is not always the year its
   * period covers. A December export firing on 1 January sits in the new year's folder and is
   * named for last year's month; both are right, and the list says so when they disagree.
   *
   * Null for a file indexed before the year level existed. Nothing has moved it.
   */
  yearFolder?: number | null;
}

/** A filter value and how many files carry it, so the counts need no second request. */
export interface PickupFacet { key: string; label: string; count: number; }

export interface PickupSearchResponse {
  files: PickupFileSummary[];
  total: number;
  clients: PickupFacet[];
  formats: PickupFacet[];
  /** Distinct render identities in the current result scope, so "whose view is this?" is filterable. */
  renderedAs: PickupFacet[];
}

export interface PickupDeliveryDto {
  id: string;
  producer: PickupProducer;
  sourceSubscriptionId: string;
  pairedPbirsSubscriptionId?: string | null;
  reportName: string;
  reportPath?: string | null;
  clientKey?: string | null;
  clientName?: string | null;
  headOfficeId?: string | null;
  format: string;
  /** Share-relative folder: "{head office id} - {client}/{subscription}". */
  storagePath: string;
  /** The UNC handed to the producer, so the BI team can see exactly what was configured. */
  uncPath: string;
  clientFolder: string;
  subscriptionFolder: string;
  isEnabled: boolean;
  ownerObjectId: string;
  ownerDisplayName: string;
  createdUtc: string;
  lastFileUtc?: string | null;
  expectedByUtc?: string | null;
  fileCount: number;
  /** A scheduled run should have produced a file by now and none arrived. */
  isOverdue: boolean;
  renderedAsEmail?: string | null;
  renderedAsName?: string | null;
  renderedAsRoleId?: number | null;
  renderedAsRoleName?: string | null;
}

export interface PickupDownloadRecord {
  id: number;
  occurredUtc: string;
  actorObjectId: string;
  actorDisplayName: string;
  fileId: string;
  fileName: string;
  clientName?: string | null;
  reportName?: string | null;
  sizeBytes: number;
  correlationId?: string | null;
  ipAddress?: string | null;
}

export interface EnablePickupRequest {
  subscriptionId: string;
  producer: PickupProducer;
  clientKey?: string | null;
  clientName?: string | null;
  /**
   * The exact head office id from the Profiles API (see {@link HeadOfficeMatch}). Optional — the
   * portal resolves it from the client name when it is not supplied — but supplying it from a
   * chosen typeahead match is what makes the folder name exact rather than a best-effort guess.
   */
  headOfficeId?: string | null;
}

export interface PickupStatusResponse { isConfigured: boolean; }

/**
 * One head office as the Profiles API's exact-match search returns it — the authority for the
 * pickup folder name (`{headOfficeId} - {name}`, verbatim). See `PickupHeadOfficePicker`.
 */
export interface HeadOfficeMatch {
  headOfficeId: number;
  name: string;
}

/**
 * The pickup head-office typeahead's answer. `available: false` means Profiles could not actually
 * be asked — no APIM subscription key configured, or the call itself failed (wrong/unauthorised
 * key, Profiles API unreachable, ...) — as opposed to a genuine search that matched nothing. See
 * `PickupHeadOfficePicker`: the two must render differently, or a failed lookup looks identical to
 * a confirmed "no such head office".
 */
export interface HeadOfficeSearchResponse {
  matches: HeadOfficeMatch[];
  available: boolean;
}

/**
 * Whether a subscription's pickup route is intact, and if not, how it is broken.
 *
 * "Email these people AND file it to pickup" is two subscriptions on the report server, because a
 * PBIRS subscription carries exactly one delivery extension. The portal creates the pair and keeps
 * it in step — but Report Manager can delete, disable, repoint or re-schedule either half behind
 * its back, and a quietly half-broken pair is worse than a visible warning.
 *
 * - `Paired` — on, and the companion is present, enabled and matching.
 * - `CompanionMissing` — the file-share subscription has gone. Nothing is being written.
 * - `CompanionDisabled` — the email still goes out; the file silently stopped.
 * - `CompanionDiverged` — it runs, but produces something other than what is emailed.
 * - `Unknown` — the report server could not be asked. Not the same as "it's fine".
 */
export type SubscriptionPickupHealth =
  | 'None'
  | 'Paired'
  | 'CompanionMissing'
  | 'CompanionDisabled'
  | 'CompanionDiverged'
  | 'Unknown';

export interface SubscriptionPickupState {
  health: SubscriptionPickupHealth;
  deliveryId?: string | null;
  /** The companion file-share subscription on MANDW01. Null once it has gone missing. */
  pairedSubscriptionId?: string | null;
  clientName?: string | null;
  storagePath?: string | null;
  uncPath?: string | null;
  fileCount: number;
  lastFileUtc?: string | null;
  isOverdue: boolean;
  /** What specifically drifted, in words — never a code. Null when nothing has. */
  driftDetail?: string | null;
}

/** The three broken states, as against "on and fine" or "could not check". */
export function hasPickupDrifted(state?: SubscriptionPickupState | null): boolean {
  return state?.health === 'CompanionMissing'
    || state?.health === 'CompanionDisabled'
    || state?.health === 'CompanionDiverged';
}

// ── Catalogue visibility ───────────────────────────────────────────────────────────────

/**
 * An admin-managed rule hiding part of the PBIRS catalogue from non-admins.
 *
 * These only ever subtract: a rule cannot grant sight of a report the caller's own report-server
 * permissions already deny. Enforcement is server-side on every endpoint that names a report —
 * this list is a management view of it, not the control itself.
 */
export interface HiddenCatalogRule {
  id: number;
  path: string;
  /** 'Folder' covers the folder and everything under it; 'Item' covers exactly one report. */
  scope: 'Item' | 'Folder';
  reason?: string | null;
  createdBy: string;
  createdUtc: string;
}

export interface CreateHiddenCatalogRuleRequest {
  path: string;
  scope: 'Item' | 'Folder';
  reason?: string | null;
}

/** What the API believes about the caller's catalogue visibility, for the admin screen. */
export interface VisibilityDiagnostics {
  mode: string;
  isAdmin: boolean;
  permissionsResolved: boolean;
  visibleReportCount: number;
  hiddenRuleCount: number;
  detail?: string | null;
}

// ── Report packs ───────────────────────────────────────────────────────────────────────
// A reusable, ordered list of reports an admin applies to one head office in a single pass.
// Mirrors Domain.Contracts/ReportPacks.cs; the enums arrive as strings, via the API's
// JsonStringEnumConverter.

/**
 * Which estate a pack item creates its subscription on. Carried on the item rather than inferred
 * from which fields are populated, because the two are created through completely different
 * services.
 */
export type ReportPackProducer = 'Pbirs' | 'KtInsights';

/**
 * Where a pack's output goes. Pack-level rather than item-level on purpose: an address that differs
 * per report is a signal the reports want managing individually, and the flow for that is to apply
 * the pack and then edit the subscriptions it created.
 */
export type ReportPackDeliveryMode = 'Pickup' | 'PickupAndEmail';

/**
 * What a pack item's head-office parameter takes from the customer being applied to.
 *
 * **A hint, not the value written.** What a subscription receives always comes from the report's own
 * parameter list — the `value` of the entry matching this customer — because a parameter offering
 * `value: "199159"`, `label: "University of Leeds"` accepts the id and nothing else. This decides
 * which field is preferred when an id and a name would both match, and is only the literal written
 * when the report's list cannot be read at all.
 */
export type ReportPackBindingSource = 'HeadOfficeId' | 'HeadOfficeName';

export interface ReportPackSummary {
  id: string;
  name: string;
  description?: string | null;
  isEnabled: boolean;
  deliveryMode: ReportPackDeliveryMode;
  /**
   * The pack's one schedule, in words, so the library can show it per row. A pack HAS one schedule
   * (see {@link ReportPackDetail.schedule}), so there is a single honest sentence to show here —
   * which there would not be if the items could disagree.
   */
  scheduleDescription: string;
  itemCount: number;
  /** How many head offices this pack has been applied to and still has live placements for. */
  customerCount: number;
  createdUtc: string;
  modifiedUtc?: string | null;
  lastAppliedUtc?: string | null;
}

export interface ReportPackDetail {
  id: string;
  name: string;
  description?: string | null;
  isEnabled: boolean;
  deliveryMode: ReportPackDeliveryMode;
  /** Null unless `deliveryMode` is PickupAndEmail. One address for every item. */
  packEmail?: string | null;
  /**
   * When this pack runs. **One schedule for the whole pack, and there is deliberately no per-item
   * override.**
   *
   * It used to live on every item, which meant setting the same recurrence once per report for a
   * single decision — four chances to get it wrong, and four values a reader had to compare before
   * they could answer "when does this pack run?". A weekly pack, a monthly pack and a quarterly
   * pack are three packs and would never be merged.
   *
   * The per-item schedule was also doing no work the stagger is not already doing better: the only
   * difference anybody set between two items on one pack was a few minutes of contention control,
   * and that is now derived from the item's ordinal at apply time.
   */
  schedule: ScheduleDefinition;
  items: ReportPackItemDto[];
  createdUtc: string;
  createdBy: string;
  modifiedUtc?: string | null;
  modifiedBy?: string | null;
}

/**
 * One report in a pack, with everything needed to create its subscription except the customer.
 *
 * Carries both a PBIRS shape and a KT Insights shape with the other side's fields null, rather than
 * being two types: the two are ordered together in one list, reordered together and rendered in one
 * table, and a discriminated pair would push a type test into every one of those places to express
 * a difference that only matters at apply time. `producer` says which half is real, and the API
 * validates that the other half is absent rather than trusting the caller.
 *
 * **An item carries no schedule and no offset.** The recurrence belongs to the pack
 * ({@link ReportPackDetail.schedule}) because a pack has exactly one, and the few minutes that keep
 * a pack's items off each other's toes are DERIVED from `ordinal` at apply time — five per position
 * — rather than authored. A stored offset goes stale the moment somebody reorders the items and
 * then disagrees with the list the author is looking at; deriving it means the order you put items
 * in is the order they run.
 */
export interface ReportPackItemDto {
  id: string;
  ordinal: number;
  producer: ReportPackProducer;
  /**
   * What this item is called on screen. Never derived from the report name alone, because two items
   * on one pack routinely differ only by their view or their pages.
   */
  title: string;

  // ── PBIRS items ──
  reportPath?: string | null;
  renderFormat?: string | null;
  /**
   * Values the pack pins for every customer — view, products, date filter, date period. The head
   * office is NOT in here; it arrives at apply time via `headOfficeParameterName`.
   */
  parameters?: ReportParameterValue[] | null;
  /**
   * The one parameter that receives the customer's head office.
   *
   * **Country is deliberately not bound this way.** On "Any Client Detail (Custom View)" Country
   * sits ABOVE HeadOfficeId in the cascade and its values are report-specific ids (1 = UK, 3 = US),
   * while /Sales/Scope 3 spells the same idea as `CountryCode` over a different value domain. So
   * country is pinned in `parameters` like any other fixed value, a pack is implicitly regional,
   * and the preview VERIFIES the head office appears in that country's resolved list rather than
   * assuming it.
   */
  headOfficeParameterName?: string | null;
  headOfficeBinding: ReportPackBindingSource;

  // ── KT Insights items ──
  /** PowerBI.Embed.Report id. Presets are gone; a KT Insights item names a real report. */
  ktReportId?: number | null;
  ktReportName?: string | null;
  ktPageOrderIdsCsv?: string | null;
  ktPageLabels?: string[] | null;
  /**
   * The filters the export runs with. `headOffice` is overwritten at apply time with the customer
   * being applied to and must not be relied on here.
   */
  ktParameters?: KtInsightsParameters | null;
  /**
   * The identity the export renders as, which decides what is IN the file. Null means "the service
   * identity", resolved from configuration at apply time. See
   * {@link ReportPackPreviewItem.renderIdentityWarning} for why this is surfaced rather than left
   * implicit.
   */
  ktRenderAsEmail?: string | null;
}

export interface SaveReportPackRequest {
  name: string;
  description?: string | null;
  isEnabled: boolean;
  deliveryMode: ReportPackDeliveryMode;
  packEmail?: string | null;
  /** The pack's one schedule — see {@link ReportPackDetail.schedule}. */
  schedule: ScheduleDefinition;
  items: ReportPackItemDto[];
}

/**
 * Ask what applying a pack to a head office would do. Performs no writes and is safe to re-run,
 * which is what lets the wizard show it before anything is committed and lets the SPA re-request it
 * whenever the customer changes.
 */
/**
 * One item's parameters adjusted for the customer being applied to, rather than for the pack.
 *
 * Two customers can want the same report on the same schedule sliced differently — last month's
 * Scope 3 by travel date for one and by invoice date for another — and those are one pack, not two.
 *
 * It is also the only place some parameters can be answered at all: a parameter whose valid values
 * come from a dataset that takes the head office as an argument has no list until a customer is
 * known, so `CustomerId` on /Sales/Scope 3 and `DateFilterId` on Any Client Detail (Custom View)
 * cannot be picked while the pack is being written.
 *
 * An override replaces the pack's value for the parameter it names, matched case-insensitively, and
 * an entry with no values clears the pack's value rather than pinning an empty one. It may not name
 * the item's `headOfficeParameterName`: the server drops such an entry rather than honouring it.
 */
export interface ReportPackItemOverride {
  itemId: string;
  parameters: ReportParameterValue[];
}

export interface PreviewReportPackRequest {
  packId: string;
  headOfficeId: number;
  headOfficeName: string;
  /**
   * The per-customer adjustments made so far. Sent with the PREVIEW as well as the apply, because
   * a preview describing the pack's own values would be describing something other than what the
   * button underneath it is about to create — and the missing-required-parameter refusal is decided
   * from the effective set.
   */
  overrides?: ReportPackItemOverride[] | null;
}

/**
 * Apply a pack. `itemIds` is the set the caller was shown and accepted — echoing it back rather
 * than re-deriving it means a pack edited between preview and apply creates what the person saw,
 * not what the pack has since become.
 */
export interface ApplyReportPackRequest {
  packId: string;
  headOfficeId: number;
  headOfficeName: string;
  itemIds: string[];
  /** Who manages these in the portal. Defaults to the caller when null. */
  portalOwnerObjectId?: string | null;
  portalOwnerDisplayName?: string | null;
  /**
   * The per-customer adjustments the admin made, echoed back for the same reason `itemIds` is: the
   * apply re-runs the preview rather than trusting the client's copy, and it has to re-run it
   * against the values the person was actually looking at.
   */
  overrides?: ReportPackItemOverride[] | null;
}

/**
 * The things a PBIRS report has to get right before a pack can put it in front of a customer.
 *
 * - `HeadOffice` — the parameter that identifies the customer: named `HeadOfficeId`, holding the id.
 * - `Dates` — a window that recomputes on every run, never a pinned literal.
 * - `Credentials` — the data source connects as a stored account, so the report can run unattended.
 * - `Answerable` — every parameter the report insists on can actually be given a value.
 * - `Identity` — the report's value lists resolve for the portal's service account, not only for a
 *   person.
 *
 * `Credentials` is checked first and suppresses the last two when it fires: a data source that
 * prompts empties every dataset on the report, so those rules would report the same fault once per
 * parameter and point each one at a data grant that is not missing.
 */
export type ReportPackRule = 'HeadOffice' | 'Dates' | 'Credentials' | 'Answerable' | 'Identity';

export type ReportPackFindingSeverity = 'Blocks' | 'Warns';

export interface ReportPackEligibilityFinding {
  rule: ReportPackRule;
  severity: ReportPackFindingSeverity;
  /** The parameter this is about, where it is about one. */
  parameter?: string | null;
  /** What is wrong, in one sentence an account manager could repeat to BI. */
  problem: string;
  /** What has to change on the report. Nearly always somebody else's job — none of it is fixable here. */
  fix: string;
}

/** Whether a report meets the pack standard. See the `ReportPackRule` values for the rules. */
export interface ReportPackEligibility {
  reportPath: string;
  /** False when any finding blocks. The API refuses to save a pack naming an ineligible report. */
  eligible: boolean;
  /**
   * The report's head-office parameter as actually spelled, so the editor can bind it without the
   * author picking from a list. Null when the report has none, which is itself blocking.
   */
  headOfficeParameterName?: string | null;
  /**
   * The parameter that decides whose data the report can see — reported only when setting it was
   * PROVED to change what the report returns. A parameter that looks like an identity and changes
   * nothing is deliberately not named here, because pinning it would not help.
   */
  identityParameterName?: string | null;
  /** True when the report's lists only resolve once an identity is pinned. Usable, with a warning. */
  requiresPinnedIdentity: boolean;
  findings: ReportPackEligibilityFinding[];
}

/** Whether one item of a pack can be created for this customer, and if not, why not. */
export type ReportPackPreviewOutcome =
  /** Will be created. */
  | 'WillCreate'
  /**
   * Already exists for this customer from an earlier apply. Applying again skips it rather than
   * creating a second copy — a double-apply must not silently double a customer's subscriptions.
   */
  | 'AlreadyExists'
  /**
   * Cannot be created and will be skipped, with every other item still created.
   * `reason` says why in words a person can act on.
   */
  | 'Blocked';

export interface ReportPackPreviewItem {
  itemId: string;
  ordinal: number;
  title: string;
  producer: ReportPackProducer;
  outcome: ReportPackPreviewOutcome;
  /** Why this item is blocked or skipped, in words. Null when it will simply be created. */
  reason?: string | null;
  /** What the subscription will actually run with, for the reviewer to check. */
  parametersDescription: string;
  scheduleDescription: string;
  renderFormat?: string | null;
  /** Set when the item already exists, so the reviewer can go and look at it. */
  existingSubscriptionId?: string | null;
  /**
   * Stated for every KT Insights item, never inferred. An export renders AS its recipient, and an
   * Internal identity has NO row-level filter applied — so the head-office filter is the only thing
   * scoping the file, and whoever approves this apply has to be told that in the moment they
   * approve it rather than discovering it from a customer's pickup folder. Never suppress it.
   */
  renderIdentityWarning?: string | null;
  /**
   * Something qualifying an item that WILL still be created — never a refusal.
   *
   * Deliberately separate from `reason`, and the two must not be merged. A reason explains why
   * something did NOT happen and only ever appears on a Blocked or AlreadyExists item; a note
   * qualifies something that IS about to happen and appears on items whose outcome is WillCreate.
   * Rendered like the other warnings, in full, in the row — never behind a tooltip.
   */
  advisoryNote?: string | null;
  /**
   * Every parameter value this item would be created with: the pack's pinned values, this
   * customer's overrides on top, and the bound head office written last as the value the report's
   * own list holds for them.
   *
   * The editor in the wizard is seeded from this rather than from the pack, so what is on screen is
   * what the server will write. Re-deriving it here would be a second implementation of the
   * precedence rules and of the head-office substitution, and the two would disagree.
   */
  effectiveParameters?: ReportParameterValue[] | null;
  /**
   * Required parameters this item still has no value for, by display name. Present only on the
   * refusal they cause — the wizard opens the parameter editor straight onto them.
   */
  missingRequiredParameters?: string[] | null;
}

export interface ReportPackApplyPreview {
  packId: string;
  packName: string;
  headOfficeId: number;
  headOfficeName: string;
  /**
   * The pickup folder every item will file into — `{headOfficeId} - {name}`, taken verbatim from
   * Profiles. Shown because filing under the wrong client is the failure that matters most here and
   * is invisible until somebody downloads the wrong customer's data.
   */
  pickupClientFolder: string;
  /**
   * Minutes this customer's items are shifted by, on top of each item's own offset. Derived
   * deterministically from the head office id, so re-applying puts a customer back in the same slot
   * rather than reshuffling the whole estate's run times.
   */
  scheduleSpreadMinutes: number;
  items: ReportPackPreviewItem[];
  /**
   * False when the Profiles API could not be reached, so the head office could not be confirmed.
   * Deliberately distinct from "no such head office": an unanswerable question and a negative
   * answer are not the same fact, and only one of them means the customer does not exist.
   */
  headOfficeConfirmed: boolean;
}

export interface ReportPackApplyItemResult {
  itemId: string;
  title: string;
  created: boolean;
  subscriptionId?: string | null;
  /** Why this one did not get created. Null on success. */
  error?: string | null;
  /**
   * True when the subscription was created but its pickup route was not. Those are different
   * answers: the report will run and email, and nothing will ever arrive in the folder.
   */
  pickupFailed: boolean;
}

export interface ReportPackApplyResult {
  packId: string;
  packName: string;
  enrolmentId: string;
  headOfficeId: number;
  headOfficeName: string;
  appliedUtc: string;
  items: ReportPackApplyItemResult[];
  /**
   * C# computed properties, so they do arrive on the wire — but optional here, and the results
   * screen counts `items` itself rather than reading them. A count that could be absent must never
   * be the thing that decides whether failures are mentioned.
   */
  createdCount?: number;
  failedCount?: number;
}

/**
 * Whether a subscription a pack created still matches the pack, and if not, how it differs.
 *
 * Same discipline as {@link SubscriptionPickupHealth}, applied to a whole customer's suite instead
 * of one companion subscription. Nothing here repairs itself: a person edited that subscription for
 * a reason, and overwriting them without saying so is worse than the drift.
 *
 * - `InSync` — the subscription still matches what the pack says it should be.
 * - `LocallyEdited` — it exists and runs, but its report, parameters, schedule or format no longer
 *   match the pack. Skipped by a pack roll-out unless the admin explicitly includes it.
 * - `Missing` — the subscription is no longer on the report server. Nothing is being produced.
 * - `Unknown` — the report server could not be asked. Deliberately not reported as healthy.
 */
export type ReportPackPlacementHealth = 'InSync' | 'LocallyEdited' | 'Missing' | 'Unknown';

export interface ReportPackPlacementDto {
  id: string;
  enrolmentId: string;
  packItemId: string;
  itemTitle: string;
  producer: ReportPackProducer;
  subscriptionId: string;
  health: ReportPackPlacementHealth;
  /** What specifically differs, in words, for the banner. Null when nothing does. */
  driftDetail?: string | null;
  lastCheckedUtc?: string | null;
  /**
   * Set when an admin has seen this local edit and chosen to keep it. Stops the pack nagging about
   * a difference somebody has already decided is correct.
   */
  localEditAcknowledged: boolean;
  /**
   * What this customer was given that the pack does not say — the overrides applied at apply time.
   * Null on a placement created with none.
   *
   * A placement that differs from the pack on purpose and one that has drifted must not read the
   * same: without this, a roll-out would see "this customer wants invoice date" as drift and put it
   * back to the pack's travel date.
   */
  customerParameters?: ReportParameterValue[] | null;
}

export interface ReportPackEnrolmentDto {
  id: string;
  packId: string;
  packName: string;
  headOfficeId: number;
  headOfficeName: string;
  portalOwnerObjectId: string;
  portalOwnerDisplayName: string;
  isEnabled: boolean;
  enrolledUtc: string;
  enrolledBy: string;
  placements: ReportPackPlacementDto[];
  /** C# computed property — see {@link ReportPackApplyResult.createdCount} for the same caveat. */
  needsAttentionCount?: number;
}

/**
 * How many placements of an enrolment want a human to look at them.
 *
 * Counted here rather than read from `needsAttentionCount` so a build that does not send it cannot
 * make a customer's broken placements report as zero. `Unknown` counts — "I could not check" is not
 * "I checked and it is fine" — and an acknowledged local edit does not, because somebody has
 * already decided that difference is correct.
 */
export function placementsNeedingAttention(enrolment: ReportPackEnrolmentDto): number {
  return enrolment.placements.filter(p =>
    p.health === 'Missing'
    || p.health === 'Unknown'
    || (p.health === 'LocallyEdited' && !p.localEditAcknowledged)).length;
}

/**
 * What happened to one subscription while stopping a customer's enrolment. Reported per item and
 * never as one verdict, for the same reason {@link ReportPackApplyItemResult} is.
 */
export interface ReportPackUnenrolItemResult {
  subscriptionId: string;
  itemTitle: string;
  deleted: boolean;
  /** Why this one is still running. Null when it was deleted. */
  error?: string | null;
}

/**
 * The outcome of stopping a customer's enrolment.
 *
 * `enrolmentRemoved` is false whenever any subscription survived: removing the enrolment then would
 * orphan those subscriptions, still running and no longer findable from the pack that made them. A
 * false here is a failure to report, never a success to celebrate.
 */
export interface ReportPackUnenrolResult {
  enrolmentId: string;
  headOfficeId: number;
  headOfficeName: string;
  enrolmentRemoved: boolean;
  items: ReportPackUnenrolItemResult[];
  /** C# computed properties — see {@link ReportPackApplyResult.createdCount} for the same caveat. */
  deletedCount?: number;
  failedCount?: number;
}

// ── Any Client Detail custom views (admin only, behind the customViews feature flag) ──────

/**
 * The Any Client Detail configuration on MANDW01.ReportData: every view (one row each in
 * DynamicReportView) and every selectable field (DynamicReportField, shared across all views).
 * `source` is 'live' | 'sample' | 'unconfigured' — the same vocabulary as the KT Insights reads.
 */
export interface CustomViewCatalog {
  source: 'live' | 'sample' | 'unconfigured';
  reportId: number;
  reportName: string;
  views: DynamicReportView[];
  fields: DynamicReportField[];
  /**
   * Per-view default parameter values. NULL — as opposed to empty — means the table could not be
   * read (its schema is documented nowhere the portal could verify), and must render as
   * "not readable", never as "none".
   */
  parameterDefaults?: CustomViewParameterDefault[] | null;
}

/**
 * One per-view default. Exactly one of `defaultValue` (a literal — comma-separated when
 * `isMultiSelect`) and `defaultValueSql` (an expression: relative dates like "start of next week")
 * is populated per row.
 */
export interface CustomViewParameterDefault {
  viewId: number;
  parameter: string;
  defaultValue?: string | null;
  defaultValueSql?: string | null;
  notes?: string | null;
  isMultiSelect?: boolean;
}

export interface DynamicReportView {
  viewId: number;
  viewDescription: string;
  /** The rendered report header; defaults to "Client Detail" when null. */
  label?: string | null;
  /** Comma-separated FieldIds in display order — varchar(1000) on the server. */
  fieldIdList: string;
  distinct: boolean;
  securityGroups?: string | null;
  /** When populated, ONLY these usernames see the view — overrides securityGroups entirely. */
  userList?: string | null;
  live: boolean;
  notes?: string | null;
  /** When distinct: the FieldIds aggregated (each with its field's function, default MAX). */
  aggregateFieldIdList?: string | null;
  /** FieldIds the view sorts by. Saves remap this through renames/removals automatically. */
  orderByFieldIdList?: string | null;
  /**
   * A per-view row filter / result transform: described for humans, implemented in SQL the portal
   * never edits — clones inherit both, edits leave them alone. The booleans say the SQL exists.
   */
  filterDescription?: string | null;
  hasFilterSql?: boolean;
  transformDescription?: string | null;
  hasTransformSql?: boolean;
}

export interface DynamicReportField {
  fieldId: number;
  /** The join(s) the field depends on, |-delimited — drives the proc's join pruning. */
  tableAlias?: string | null;
  /** The column header exactly as rendered. Must be unique within a view. */
  fieldAlias: string;
  fieldSql: string;
  notes?: string | null;
  /** True for the fields that double as the report's date-filter choices (8/9/10/11). */
  dateFilter?: boolean;
  /** How the field aggregates on a Distinct view. Null = the proc's default, MAX. */
  aggregateFunction?: string | null;
}

/** One column of a draft: an existing field, optionally shown under a customer's own header. */
export interface CustomViewColumn {
  sourceFieldId: number;
  /** Non-null = new DynamicReportField row over the same FieldSQL (or a reuse on exact match). */
  rename?: string | null;
  /** On a Distinct view: aggregate this column instead of grouping by it. */
  aggregate?: boolean;
}

export interface CustomViewDraft {
  viewDescription: string;
  baseViewId: number;
  columns: CustomViewColumn[];
  /** Set when editing an existing view; null/absent creates a new one. */
  editingViewId?: number | null;
  /** Null inherits the base's flag on create / keeps the current one on edit. */
  distinct?: boolean | null;
  /** The rendered report header. Null = the view name on create / keep the stored one on edit. */
  label?: string | null;
  /**
   * The COMPLETE desired default set. Null = copy the base's on create / keep the stored ones on
   * edit (sent only when the existing defaults could not be read, so nothing unseen is replaced);
   * a list — even an empty one — replaces the view's whole set.
   */
  parameterDefaults?: CustomViewParameterDefaultInput[] | null;
}

/** One desired default: exactly one of defaultValue / defaultValueSql must be set. */
export interface CustomViewParameterDefaultInput {
  parameter: string;
  defaultValue?: string | null;
  defaultValueSql?: string | null;
  notes?: string | null;
  isMultiSelect?: boolean;
}

/** What an apply returned: the view as it now exists. */
export interface CustomViewApplyResult {
  viewId: number;
  viewDescription: string;
}

export interface CustomViewIssue {
  severity: 'error' | 'warning' | 'info';
  code: string;
  message: string;
}

export interface CustomViewResolvedColumn {
  position: number;
  header: string;
  tableAlias?: string | null;
  fieldId?: number | null;
  copyOfFieldId?: number | null;
  reusedExistingField: boolean;
  aggregate?: boolean;
}

/** What the composer made of a draft: validation issues and the resolved column layout. */
export interface CustomViewValidationResult {
  issues: CustomViewIssue[];
  layout: CustomViewResolvedColumn[];
}
